import React, { useState } from 'react';
import { ShareNetwork, Link as LinkIcon, Check, WhatsappLogo, XLogo, TelegramLogo } from '@phosphor-icons/react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Button } from './ui/button';
import { toast } from 'sonner';

const FACEBOOK_ICON = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current"><path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.988h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"/></svg>
);

const PLATFORMS = [
  { id: 'whatsapp', label: 'WhatsApp', Icon: WhatsappLogo, color: 'hover:text-green-400' },
  { id: 'facebook', label: 'Facebook', Icon: FACEBOOK_ICON, color: 'hover:text-blue-400' },
  { id: 'x', label: 'X (Twitter)', Icon: XLogo, color: 'hover:text-white' },
  { id: 'telegram', label: 'Telegram', Icon: TelegramLogo, color: 'hover:text-sky-400' },
];

export function ShareMenu({ url, ogUrl, title, variant = 'icon', className = '' }) {
  const [copied, setCopied] = useState(false);
  const origin = window.location.origin; // gospeleon.com in production
  const text = title || 'Check this out on Gospeleon';

  // Extract just the path from ogUrl, stripping any domain prefix
  // e.g., "https://emergent.host/api/og/artist/slug" → "/api/og/artist/slug"
  // or "/api/og/artist/slug" stays as-is
  let ogPath = '';
  if (ogUrl) {
    try {
      ogPath = new URL(ogUrl, origin).pathname;
    } catch {
      ogPath = ogUrl.startsWith('/') ? ogUrl : `/${ogUrl}`;
    }
  }
  const shareUrl = ogPath ? `${origin}${ogPath}` : `${origin}${window.location.pathname}`;

  // Share redirect goes through /api/share/:platform on the CURRENT domain
  const getShareHref = (platform) => {
    return `${origin}/api/share/${platform}?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(text)}`;
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Link copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy link');
    }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        {variant === 'icon' ? (
          <button className={`text-white/40 hover:text-white transition-colors ${className}`} data-testid="share-btn">
            <ShareNetwork className="w-5 h-5" />
          </button>
        ) : (
          <Button variant="outline" className={`border-white/10 text-white/70 hover:bg-white/5 rounded-full ${className}`} data-testid="share-btn">
            <ShareNetwork className="mr-2 w-4 h-4" />Share
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent className="w-56 bg-[#1A1A1F] border-white/10 p-2" align="end" sideOffset={8}>
        <p className="text-xs text-white/40 px-2 py-1.5 font-medium uppercase tracking-wider">Share via</p>
        {PLATFORMS.map(({ id, label, Icon, color }) => (
          <a
            key={id}
            href={getShareHref(id)}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-white/70 ${color} hover:bg-white/5 transition-colors`}
            data-testid={`share-${id}`}
          >
            <Icon className="w-5 h-5" />{label}
          </a>
        ))}
        <div className="h-px bg-white/5 my-1" />
        <button
          onClick={handleCopy}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-white/70 hover:text-[#F59E0B] hover:bg-white/5 transition-colors"
          data-testid="share-copy-link"
        >
          {copied ? <Check className="w-5 h-5 text-green-400" /> : <LinkIcon className="w-5 h-5" />}
          {copied ? 'Copied!' : 'Copy link'}
        </button>
      </PopoverContent>
    </Popover>
  );
}
